import { create } from 'zustand'
import type { KeyMeta } from '@/lib/keyStore'
import { KeyStore, getDevicePassphrase } from '@/lib/keyStore'

interface KeysState {
  keys: KeyMeta[]
  loading: boolean
  _decryptedCache: Record<string, string | null>
}

interface KeysActions {
  init: () => Promise<void>
  addKey: (provider: string, label: string, rawKey: string) => Promise<void>
  removeKey: (provider: string) => Promise<void>
  getKey: (provider: string) => Promise<string | null>
}

let _keyStore: KeyStore | null = null

function getKeyStore(): KeyStore {
  if (!_keyStore) throw new Error('KeyStore not initialised — call init() first')
  return _keyStore
}

export const useKeysStore = create<KeysState & KeysActions>()((set, get) => ({
  keys: [],
  loading: true,
  _decryptedCache: {},

  init: async () => {
    if (_keyStore) return
    const passphrase = getDevicePassphrase()
    _keyStore = new KeyStore(passphrase)
    const keys = await _keyStore.listKeys()
    set({ keys, loading: false })
  },

  addKey: async (provider, label, rawKey) => {
    await getKeyStore().saveKey(provider, label, rawKey)
    const meta: KeyMeta = { provider, label, lastFour: rawKey.slice(-4) }
    set((state) => ({
      keys: state.keys.filter((k) => k.provider !== provider).concat(meta),
      _decryptedCache: { ...state._decryptedCache, [provider]: rawKey },
    }))
  },

  removeKey: async (provider) => {
    await getKeyStore().removeKey(provider)
    set((state) => {
      const { [provider]: _evicted, ...rest } = state._decryptedCache
      return {
        keys: state.keys.filter((k) => k.provider !== provider),
        _decryptedCache: rest,
      }
    })
  },

  getKey: async (provider) => {
    const cached = get()._decryptedCache[provider]
    if (cached !== undefined) return cached
    const key = await getKeyStore().getKey(provider)
    set((state) => ({ _decryptedCache: { ...state._decryptedCache, [provider]: key } }))
    return key
  },
}))
